import type { ShopperBasketsTypes } from "commerce-sdk-isomorphic";
import {
  Link,
  data as responseData,
  useActionData,
  useFetcher,
  useNavigation,
  useSearchParams,
} from "react-router";
import { namedAction } from "remix-utils/named-action";
import { z } from "zod";

import {
  commitSession,
  isAuthenticatedUser,
  resetSessionWithOldData,
} from "~/.server/sessions.server";
import { validationError } from "~/components/forms/validationErrorResponse.server";
import { assertIsPost, safeRedirect } from "~/lib/utils-server/http.server";
import { getPageMeta } from "~/seo/servers/seo.server";

import { withZod } from "@rvf/zod";

import { Form, InputField, PasswordField } from "~/components/forms/Form";
import { FormErrors } from "~/components/forms/FormErrors";
import { GlobalErrors } from "~/components/forms/GlobalErrors";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { H1 } from "~/components/ui/text";
import { TriButton } from "~/components/ui/tri-button";
import config from "~/config/default";
import { useURL } from "~/contexts";
import { EMPTY_BASKET_CODE } from "~/shop/empty.basket";

import { mergeBaskets } from "../checkout/utils/merge-basket";
import { mergeStore } from "../checkout/utils/merge-store";
import { guestWishlistUtils } from "../resources/utils/guest-wishlist-utils";

export const loginSchema = z.object({
  email: z.string().email().min(1),
  password: z.string().min(6).max(66),
  guestWishlist: z.string().optional(),
});
export const resetPasswordSchema = z.object({
  email: z.string().email().min(1).or(z.literal("")),
});
export const resetPasswordSchemaServer = z.object({
  email: z.string().email().min(1),
});

export const loginSchemaValidator = withZod(loginSchema);
export const resetPasswordValidator = withZod(resetPasswordSchema);
export const resetPasswordValidatorServer = withZod(resetPasswordSchemaServer);

export const loader = async ({ request, context }: LoaderArgs) => {
  const isLoggedin = isAuthenticatedUser();
  if (isLoggedin) {
    throw safeRedirect("/account");
  }

  const seo = getPageMeta({
    type: "login",
    options: {
      title: "Login",
      description: "The Login page of Spark* store",
    },
    robots: {
      noFollow: false,
      noIndex: true,
    },
  });

  return { seo };
};
export const action = async ({ request, context }: ActionArgs) => {
  const formData = await request.formData();
  return namedAction(formData, {
    async loginUser() {
      assertIsPost(request);
      const validation = await loginSchemaValidator.validate(formData);
      const { api, auth, shop, session } = context;
      if (validation.error)
        return validationError(validation.error, validation.submittedData);

      const basketId = context.session.data.basketId;
      const guestWishlist = validation.data.guestWishlist;
      let oldBasket: ShopperBasketsTypes.Basket | null | undefined;

      if (basketId && basketId !== EMPTY_BASKET_CODE) {
        try {
          oldBasket = await api.shopperBaskets.getBasket({
            parameters: { basketId: basketId },
          });
        } catch (e) {
          if (e instanceof Error) console.log(e.name);
          oldBasket = null;
        }
      }

      try {
        await api.auth.login({
          password: validation.data.password,
          username: validation.data.email,
        });

        if (guestWishlist) {
          await shop.mergeWishlists(JSON.parse(guestWishlist));
        }
        await mergeBaskets(api, auth, oldBasket);
        await mergeStore(shop, auth, api, session);
        await commitSession();
        throw safeRedirect("/account", {
          headers: {
            "Set-Cookie": await resetSessionWithOldData(),
          },
        });
      } catch (e) {
        if (e instanceof Error) {
          console.log(e.stack || e);

          return validationError(e.message, validation.submittedData);
        }
        throw e;
      }
    },
    async forgottenPasswordReset() {
      const {
        locale: { alias },
        api,
        auth,
      } = context;
      const {
        app: {
          commerceAPI: {
            parameters: { clientId, siteId },
          },
        },
        ssrParameters,
      } = config;

      const proxyPath = ssrParameters.proxyConfigs[1].host;

      const resetApiUrl = `${proxyPath}/s/${siteId}/dw/shop/v22_6/customers`;
      assertIsPost(request);
      const validation = await resetPasswordValidatorServer.validate(formData);

      if (validation.error)
        return validationError(validation.error, validation.submittedData);

      try {
        const token = auth.get("access_token");
        const baseUrl = request.headers.get("Referer")?.split("/login")[0];

        const headers = {
          "Content-Type": "application/json",
          "x-dw-client-id": clientId,
          "x-sf-cc-url": `${baseUrl}${
            alias !== "" && alias !== "en-GB" ? "/" + alias : ""
          }/reset-password?Email=${encodeURIComponent(validation.data.email)}`,
          Authorization: `${token}`,
        };
        // Issues with PWA https://github.com/SalesforceCommerceCloud/pwa-kit/issues/363

        // throw new Error("Not implemented");

        // const token = await api.shopperCustomers.getResetPasswordToken({
        //     body: { login: validation.data.email },
        //   });

        const response = await fetch(`https://${resetApiUrl}/password_reset`, {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            identification: validation.data.email,
            type: "login",
          }),
        });

        return responseData({
          forgottenPasswordReset: true,
          email: validation.data.email,
        });
      } catch (e) {
        if (e instanceof Error) {
          console.log("error", e.stack || e);

          return validationError(
            e.message + "from catch",
            validation.submittedData,
          );
        }
        throw e;
      }
    },
  });
};

const Login = () => {
  const transition = useNavigation();
  const fetcher = useFetcher<typeof action>();
  const actionData = useActionData<typeof action>() || fetcher.data;
  const isLoading = transition.state !== "idle" || fetcher.state !== "idle";

  const [searchParams, setSearchParams] = useSearchParams();
  const $showForgottenPassword =
    searchParams.get("forgottenPassword") === "true";
  const url = useURL();

  return (
    <div className="flex min-h-full items-center justify-center px-5 py-12 sm:px-6 lg:px-8 xl:py-[68px]">
      <div className="w-full max-w-md space-y-8 xl:max-w-[336px] 2xl:max-w-[504px]">
        <H1 className="mt-6 text-left text-2xl font-semibold tracking-tight text-contrast-black">
          Sign In to Your Account
        </H1>
        <GlobalErrors data={actionData} showFieldErrors />
        <Form
          validator={loginSchemaValidator}
          className="!mt-3 space-y-4"
          method="post"
          fetcher={fetcher}
          data-testid="login-form"
        >
          <input type="hidden" name="intent" value="loginUser" />
          <input
            type="hidden"
            name="guestWishlist"
            value={JSON.stringify(guestWishlistUtils.getWishlist())}
          />
          <InputField
            label="Email"
            name="email"
            autoComplete="username"
            type="email"
            placeholder=""
            className="mt-1 text-contrast-black"
            data-testid="email-login"
          />
          <PasswordField
            label="Password"
            autoComplete="current-password"
            name="password"
            className="mt-1 text-contrast-black"
            data-testid="password-login"
          />

          <div className="flex items-center justify-between font-light text-remove">
            <Link to={url("/login?forgottenPassword=true")}>
              Forgot Password?
            </Link>
          </div>

          <div>
            <TriButton
              type="submit"
              disabled={isLoading && fetcher.formAction?.includes("loginUser")}
              isLoading={isLoading && fetcher.formAction?.includes("loginUser")}
              className="mt-3 w-full px-6 text-base font-semibold"
            >
              Sign In
            </TriButton>
          </div>
          <Link
            to={url("/register")}
            className="inline-block w-full text-center text-base font-light text-remove"
            data-testid="create-account-button"
          >
            Create an Account
          </Link>
        </Form>
      </div>
      <Dialog
        open={$showForgottenPassword}
        onOpenChange={() =>
          setSearchParams(search => {
            search.set("forgottenPassword", "false");
            return search;
          })
        }
      >
        <DialogContent className="bg-white">
          <Form
            validator={resetPasswordValidator}
            className="mt-8 space-y-6"
            method="post"
            fetcher={fetcher}
          >
            <input type="hidden" name="intent" value="forgottenPasswordReset" />
            <GlobalErrors data={actionData} />
            <Label className="block text-lg font-medium leading-5 text-stone-900 dark:text-stone-50">
              Password Reset
            </Label>
            <Label className="block text-base font-medium leading-2 text-stone-900 dark:text-stone-50">
              Enter the email address associated with your account and
              we&apos;ll email you a link.
            </Label>
            <InputField name="email" label="Email" />
            <FormErrors />

            <TriButton
              type="submit"
              variant="outline"
              className="mb-3 mt-5 w-fit px-6 text-base font-semibold"
              disabled={fetcher.state !== "idle"}
              isLoading={fetcher.state !== "idle"}
            >
              Send email
            </TriButton>
          </Form>
        </DialogContent>
      </Dialog>
      {actionData && "forgottenPasswordReset" in actionData && (
        <Dialog
          open={
            actionData.forgottenPasswordReset === true && $showForgottenPassword
          }
          onOpenChange={() =>
            setSearchParams(params => {
              params.set("forgottenPassword", "false");
              return params;
            })
          }
        >
          <DialogContent className="bg-white">
            <Label className="text-lg font-medium leading-5 text-stone-900 dark:text-stone-50">
              Reset Password
            </Label>
            <Label className="text-base font-medium leading-2 text-stone-900 dark:text-stone-50">
              Thank you for submitting your email address. We&apos;ve emailed a
              reset password link to{" "}
              <span className="text-blue-500">{actionData.email}</span>.
            </Label>
            <Label className="text-base font-medium leading-2 text-stone-900 dark:text-stone-50">
              This exclusive link is only valid for 24 hours. If you can’t find
              the email please check your junk folder.
            </Label>
            <form action="/login" method="get">
              <Button
                type="submit"
                variant="outline"
                className="mb-3 mt-5 w-fit px-6 text-base font-semibold"
                value="Close"
              >
                Ok
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Login;
